import { Platform, Pressable, StyleSheet, ViewStyle } from 'react-native';
import { Text, View } from '@constants/Themed';
import styles from '../../../stylesheet';
import { forwardRef, useEffect, useState } from 'react';
import Ionicons from '@expo/vector-icons/Ionicons';
import * as Animatable from 'react-native-animatable';
import AntDesign from '@expo/vector-icons/AntDesign';
import * as LocalAuthentication from 'expo-local-authentication';
import { SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useDispatch, useSelector } from 'react-redux';
import React, { useRef, useImperativeHandle } from 'react';
import AchievementBackground from '@assets/--native/AchievementBackground';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { H2, H5, Separator, XStack, YStack, Popover, Button, SizableText } from 'tamagui';
import { IconButton, Menu, MenuItem } from "@mui/material";

import SmallActionButton from '@components/UI/SmallActionButton';
import { LinearGradient } from 'expo-linear-gradient';
import { useDeviceType } from '@hooks/useDeviceType';

// Hybrid Dropdown Menu component - Material-UI for web, Tamagui Popover for native
const CardActionsDropdown = ({ actions, theme }: { 
  actions: Array<{ icon: string | JSX.Element, function: (e: any) => void, color?: string, label?: string, special?: JSX.Element }>;
  theme?: boolean;
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const handleActionPress = (action: any) => {
    action.function();
    setMenuAnchor(null);
    setPopoverOpen(false);
  };

  // Web implementation using Material-UI Menu
  if (Platform.OS === 'web') {
    return (
      <>
        <IconButton
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          sx={{
            backgroundColor: theme ? "white" : "rgba(255, 255, 255, 0.2)",
            color: theme ? "#333333" : "white",
            border: `1px solid ${theme ? "#e0e0e0" : "rgba(255, 255, 255, 0.3)"}`,
            '&:hover': {
              backgroundColor: theme ? "#f5f5f5" : "rgba(255, 255, 255, 0.3)",
            },
            width: 36,
            height: 36,
            margin: 0
          }}
        >
          <Ionicons 
            name="ellipsis-vertical" 
            size={20} 
            color={theme ? "#333333" : "white"} 
          />
        </IconButton>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          PaperProps={{
            style: {
              backgroundColor: theme ? "white" : "#333333",
              border: `1px solid ${theme ? "#e0e0e0" : "#555555"}`,
              borderRadius: 8,
              minWidth: 180
            }
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {actions
            .filter(action => action != null) // Filter out null/undefined actions
            .map((action, index) => {
            // Handle special components (like duplicate functionality)
            if (action.special) {
              return (
                <MenuItem 
                  key={index}
                  sx={{
                    color: theme ? "#333333" : "white",
                    '&:hover': {
                      backgroundColor: theme ? "#f5f5f5" : "#444444",
                    },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    paddingX: 2,
                    paddingY: 1.5,
                    minHeight: 48,
                    width: '100%'
                  }}
                >
                  <View style={{ 
                    flexDirection: 'row', 
                    alignItems: 'center', 
                    width: '100%',
                    paddingHorizontal: 0
                  }}>
                    <View style={{ marginRight: 12, minWidth: 20, alignItems: 'center' }}>
                      <Ionicons 
                        name="copy" 
                        size={18} 
                        color={theme ? "#333333" : "white"} 
                      />
                    </View>
                    <View style={{ flex: 1, marginLeft: 8 }}>
                      {action.special}
                    </View>
                  </View>
                </MenuItem>
              );
            }

            // Handle regular actions
            return (
              <MenuItem 
                key={index}
                onClick={() => handleActionPress(action)}
                sx={{
                  color: theme ? "#333333" : "white",
                  '&:hover': {
                    backgroundColor: theme ? "#f5f5f5" : "#444444",
                  },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  paddingX: 2,
                  paddingY: 1.5
                }}
              >
                <View style={{ marginRight: 12, minWidth: 20, alignItems: 'center' }}>
                    {typeof action.icon === 'string' ? (
                    <Ionicons 
                      name={action.icon as any} 
                      size={18} 
                      color={action.color || (theme ? "#333333" : "white")} 
                    />
                    ) : (
                    React.cloneElement(action.icon, { 
                      color: action.color || (theme ? "#333333" : "white") 
                    })
                    )}
                </View>
                <span style={{ marginLeft: 8 }}>{action.label || `Action ${index + 1}`}</span>
              </MenuItem>
            );
          })}
        </Menu>
      </>
    );
  }

  // Native implementation using Tamagui Popover
  return (
    <Popover size="$5" allowFlip placement="bottom-end">
      <Popover.Trigger asChild>
        <Pressable
          style={{
            width: 36,
            height: 36,
            borderRadius: 18,
            backgroundColor: theme ? "white" : "rgba(255, 255, 255, 0.2)",
            justifyContent: 'center',
            alignItems: 'center',
            borderWidth: 1,
            borderColor: theme ? "#e0e0e0" : "rgba(255, 255, 255, 0.3)"
          }}
        >
          <Ionicons 
            name="ellipsis-vertical" 
            size={20} 
            color={theme ? "#333333" : "white"} 
          />
        </Pressable>
      </Popover.Trigger>

      <Popover.Content
        borderWidth={1}
        borderColor={theme ? "#e0e0e0" : "$borderColor"}
        enterStyle={{ y: -10, opacity: 0 }}
        exitStyle={{ y: -10, opacity: 0 }}
        elevate
        animation={[
          'quick',
          {
            opacity: {
              overshootClamping: true,
            },
          },
        ]}
        backgroundColor={theme ? "white" : "$background"}
        minWidth={180}
        padding="$2"
      >
        <Popover.Arrow borderWidth={1} borderColor={theme ? "#e0e0e0" : "$borderColor"} />
        
        <YStack gap="$1">
          {actions.map((action, index) => {
            // Handle special components (like duplicate functionality)
            if (action.special) {
              return (
                <View 
                  key={index} 
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingHorizontal: 12,
                    paddingVertical: 10,
                    borderRadius: 6,
                    minHeight: 44
                  }}
                >
                  <View style={{ marginRight: 12, minWidth: 20, alignItems: 'center' }}>
                    <Ionicons 
                      name="copy" 
                      size={18} 
                      color={theme ? "#333333" : "white"} 
                    />
                  </View>
                  <View style={{ flex: 1, marginLeft: 8 }}>
                    {action.special}
                  </View>
                </View>
              );
            }

            // Handle regular actions
            return (
              <Pressable
                key={index}
                onPress={() => handleActionPress(action)}
                style={({ pressed }: any) => ({
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  borderRadius: 6,
                  backgroundColor: pressed ? (theme ? "#f5f5f5" : "rgba(255, 255, 255, 0.1)") : 'transparent'
                })}
              >
                <View style={{ marginRight: 12, minWidth: 20, alignItems: 'center' }}>
                  {typeof action.icon === 'string' ? (
                    <Ionicons 
                      name={action.icon as any} 
                      size={18} 
                      color={action.color || (theme ? "#333333" : "white")} 
                    />
                  ) : (
                    action.icon
                  )}
                </View>
                <SizableText 
                  size="$4" 
                  color={theme ? "#333333" : "white"}
                  flex={1}
                >
                  {action.label || `Action ${index + 1}`}
                </SizableText>
              </Pressable>
            );
          })}
        </YStack>
      </Popover.Content>
    </Popover>
  );
};

type DropDownViewProps = {
  cardTitle?: string | JSX.Element,
  children: JSX.Element[] | JSX.Element,
  hideInitial?: boolean,
  theme?: boolean,
  secure?: boolean,
  containerStyle?: ViewStyle,
  minHeight?: number | string,
  keyString?: string,
  style?: ViewStyle,
  green?: boolean,
  color?: string,
  removeDrop?: boolean;
  fillAll?: boolean;
  extraContent?: JSX.Element | null;
  subtitle?: string | JSX.Element;
  smallCard?: boolean;
  onVisibilityChange?: Function;
  removeBorder?: boolean;
  removeFlex?: boolean;
  helpText?: string;
  maxHeight?: number;
  disablePadding?: boolean;
  withShadow?: boolean;
  cardActions?: Array<{ icon: string | JSX.Element, function: (e: any) => void, color?: string, label?: string, special?: JSX.Element }>;
  specialAction?: JSX.Element | null;
};

export type DropDownViewHandle = {
  toggleVisibility: () => void;
};

const DropDownView = forwardRef<DropDownViewHandle, DropDownViewProps>((props, ref) => {
  const [visible, setVisible] = useState(props.secure || props.hideInitial ? false : true);
  const theme = themeSelector();
  const { isTablet } = useDeviceType();
  const gradientColors: readonly [string, string, ...string[]] = theme ? ['#1E90FF', 'rgb(90,192,234)'] : ['#333333', 'black'];


    const dispatch = useDispatch();
    useEffect(() => {
      if (props.hideInitial) {
        setVisible(false);
      }
    }, [props.hideInitial]);

    useImperativeHandle(ref, () => {
      return {
        toggleVisibility: () => {
          if (Platform.OS !== 'web' && props.secure) {
            try {
              if (!visible) {
                LocalAuthentication.authenticateAsync().then((res) => {
                  if (res.success) {
                    setVisible(!visible);
                    if (props.onVisibilityChange) props.onVisibilityChange(!visible);
                  } else {
                    dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Authentication failed', type: 'warning' } });
                    if (props.onVisibilityChange) props.onVisibilityChange(!visible);
                  }
                });
              } else {
                setVisible(!visible);
                if (props.onVisibilityChange) props.onVisibilityChange(!visible);
              }
            } catch (e) {
              dispatch({ type: SHOW_AUTH_MODAL, data: { error: 'An error occurred', type: 'error' } });
            }
          } else {
            setVisible(!visible);
            if (props.onVisibilityChange) props.onVisibilityChange(!visible);
          }
        },
      };

    }, [visible]);

    const appTheme = useSelector((state: DefaultRootState) => state.app.theme.lightTheme);


    return (
      <View
        key={props.keyString}
        // easing={'ease-in-cubic'}
        // direction={'alternate'}
        style={{
          flex: !props.removeFlex && 1,
          alignSelf: 'stretch',
          elevation: 20,
          maxWidth: Platform.select({ web: undefined, default: "100%" }),
          minHeight: props.minHeight && visible ? (props.maxHeight && typeof props.minHeight === 'number' && props.maxHeight < props.minHeight ? props.maxHeight : props.minHeight) : (visible ? 400 : 120),
          minWidth: Platform.select({ web: '100%', default: undefined }),
          maxHeight: props.maxHeight ? props.maxHeight : (visible ? '100%' : Platform.select({ web: undefined, default: 120 })),
          overflow: Platform.select({ web: 'hidden', default: undefined }),
          ...props.style,
        } as any}>
        <LinearGradient
          colors={gradientColors}
          style={[
            styles.noteContainer,
            {
              flex: 1,
              overflow: 'hidden',
              backgroundColor: 'transparent',
              flexDirection: 'column',
              minWidth: props.fillAll ? '105%' : '100%',
              paddingVertical: 10,
              marginVertical: 10,
              borderRadius: props.removeBorder ? 0 : 10,
            },
          ]}>
          <View
            style={{
              minWidth: "90%",
              flex: 1,
              borderRadius: 80,
              overflow: 'visible',
              paddingHorizontal: Platform.select({ default: 0, ios: 0, android: 0 }),
              minHeight: Platform.OS === 'web' ? 70 : 30,
              backgroundColor: 'transparent',
            }}>
            <Pressable
              disabled={props.removeDrop ? true : false}
              onPress={() => {
                if (!props.secure) {
                  setVisible(!visible);
                  if (props.onVisibilityChange) props.onVisibilityChange(!visible);
                } else {
                  if (Platform.OS !== 'web') {
                    try {
                      if (!visible) {
                        LocalAuthentication.authenticateAsync().then((res) => {
                          if (res.success) {
                            setVisible(!visible);
                            if (props.onVisibilityChange) props.onVisibilityChange(!visible);
                          } else {
                            dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Authentication failed', type: 'warning' } });
                            if (props.onVisibilityChange) props.onVisibilityChange(!visible);
                          }
                        });
                      } else {
                        setVisible(!visible);
                        if (props.onVisibilityChange) props.onVisibilityChange(!visible);
                      }
                    } catch (e) {
                      dispatch({ type: SHOW_AUTH_MODAL, data: { error: 'An error occurred', type: 'error' } });
                    }
                  } else {
                    setVisible(!visible);
                    if (props.onVisibilityChange) props.onVisibilityChange(!visible);
                  }
                }
              }}
              style={{ minWidth: "100%", flex: 1, flexDirection: 'row', alignSelf: 'stretch', padding: 5 }}>
              <View style={{ flexDirection: 'row', flex: 1, backgroundColor: 'transparent', maxHeight: 120, paddingHorizontal: 10 }}>
                <H2
                  numberOfLines={1}
                  userSelect="none"
                  style={{
                    fontWeight: '600',
                    textAlign: 'left',
                    flex: 3,
                    letterSpacing: 0.7,
                    zIndex: 10,
                    font: "-moz-initial",
                    fontSize: Platform.select({ default: 18, ios: 12, android: 12 }),
                    overflow: 'visible',
                    color: visible ? 'white' : 'white',
                    minHeight: 20,
                    alignSelf: "stretch",
                    flexWrap: "wrap"
                  }}>
                  {props.cardTitle}
                </H2>
                {props.secure && Platform.OS != 'web' ? (
                  <View style={{ flex: 0.4 }}>
                    <Ionicons name="lock-open" size={16} color="white" />
                  </View>
                ) : (
                  <></>
                )}
                <XStack flex={1} justifyContent="flex-end" alignItems="center" gap={10} style={{ backgroundColor: 'transparent', zIndex: 10 }}>
                  {
                    props.cardActions && props.cardActions?.length > 0 && (
                      <XStack flex={1} left={Platform.select({ default: -55, web: 5 })} minWidth={60} gap={20} style={[styles.centerEverything, localStyles.cardSideList]}>
                        <CardActionsDropdown actions={props.cardActions} theme={appTheme} />
                      </XStack>
                    )
                  }
                </XStack>
              </View>

            </Pressable>
            {props.subtitle && (
              <View style={{
                top: Platform.select({ web: 30 }),
                flex: 1, alignSelf: "stretch", marginVertical: 5, backgroundColor: 'transparent'
              }}>
                <H5 numberOfLines={2} style={{ flexWrap: "wrap", fontStyle: 'italic', color: 'white', fontSize: Platform.select({ default: undefined, ios: 8, android: 8 }) }}>{props.subtitle}</H5>
              </View>
            )}
            {
              props.helpText && !visible &&
              (
                <View style={{ flex: 1, marginVertical: 5, backgroundColor: 'transparent', overflow: "hidden", paddingHorizontal: 15, paddingRight: 30 }}>
                  <Animatable.Text animation={'slideInDown'} style={{ fontStyle: 'italic', color: 'white', fontSize: Platform.select({ default: undefined, ios: 10 }) }}>{props.helpText}</Animatable.Text>
                </View>
              )
            }

          </View>
          <View
            style={{ left: 20, flex: visible ? 6 : 0.01, flexDirection: "row", backgroundColor: "transparent", minHeight: visible ? 200 : 0, minWidth: Platform.select({ web: "100%", default: "100%" }), maxHeight: visible ? '100%' : 0, height: visible ? '100%' : 0, }}>
            {visible || props.removeDrop ? (
              <XStack flex={3} gap={30}>
                <View style={{ flex: 1, left: Platform.select({ web: 5, default: -60 }), top: Platform.select({ default: undefined, web: -10 }), shadowRadius: 20, shadowOpacity: props.withShadow ? 0.15 : 0, minHeight: '100%', minWidth: "45%", alignContent: 'flex-start', padding: props.disablePadding ? 0 : 25, backgroundColor: appTheme ? 'white' : 'grey', borderRadius: 20, paddingVertical: 10, maxWidth: isTablet ? "97%" : Platform.select({ web: props.cardActions?.length ? "95%" : "100%", default: "80%" }), overflow: 'hidden', ...props.containerStyle }}>
                  {props.children}
                </View>
                {
                  props.extraContent && (
                    <View style={{ flex: 1, width: "45%", left: Platform.select({ web: 5, default: -60 }), top: Platform.select({ default: undefined, web: -10 }), shadowRadius: 20, shadowOpacity: props.withShadow ? 0.15 : 0, paddingBottom: props.disablePadding ? undefined : 40, minHeight: '100%', alignContent: 'flex-start', padding: props.disablePadding ? 0 : 25, backgroundColor: appTheme ? 'white' : 'grey', borderRadius: 20, paddingVertical: 10, maxWidth: isTablet ? "97%" : Platform.select({ web: props.cardActions?.length ? "95%" : "100%", default: "80%" }), overflow: 'hidden', ...props.containerStyle }}>
                      {props.extraContent}
                    </View>
                  )
                }

              </XStack>
            ) : null}
          </View>
        </LinearGradient>
        {!props.removeDrop && (
          <View style={{ top: 20, position: "absolute", right: 10 }}>
            {visible ? (
              <View style={{ flex: 1, backgroundColor: 'transparent' }}>
                <AntDesign name="up" size={24} color="white" style={{ alignSelf: 'flex-end' }} />
              </View>
            ) : (
              <View style={{ flex: 1, backgroundColor: 'transparent' }}>
                <AntDesign name="down" size={24} color="white" style={{ alignSelf: 'flex-end' }} />
              </View>
            )}
          </View>
        )}
      </View>
    );
  });

const localStyles = StyleSheet.create({
  cardSideList: {
    maxWidth: 40,
    flex: 1,
    top: -10
  }
});

export default DropDownView;
